import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import UserCreateDialog, { UserCreateDialogHandle } from 'components/UserCreateDialog/UserCreateDialog';
import { useUserDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import UserEditDialog, { UserEditDialogHandle } from 'components/UserEditDialog/UserEditDialog';
import { GMetrixButtonDropdown } from 'components/GMetrixButtonDropdown/GMetrixButtonDropdown';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { UserGMetrixTable } from 'components/UserGMetrixTable/UserGMetrixTable';
import { usePartnerCreateEdit } from 'pages/partner_create_edit/context';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { NewUser, User, UsersApi } from 'apis/UsersApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PartnerPermissions } from 'pages/partners';
import { ButtonGroup } from 'react-bootstrap';
import { useSuperState } from 'hooks';

export interface StagedUser {
    allowUserToSetPassword: boolean;
    existingUser: boolean;
    permissionsCSV: string;
    user: NewUser;
}

interface UsersSectionProps {
    onRemoveStagedUser?: (index: number) => void;
    onStageUser?: (staged: StagedUser) => void;
    onUserCountChange?: (count: number) => void;
    partnerId: number;
    partnerType: number;
    searchAutoFocus?: boolean;
    stagedMode?: boolean;
    stagedUsers?: StagedUser[];
}

export interface UsersSectionHandle {
    openAddDialog: () => void;
}

export const UsersSection = forwardRef<UsersSectionHandle, UsersSectionProps>(({ onRemoveStagedUser, onStageUser, onUserCountChange, partnerId, partnerType, searchAutoFocus, stagedMode, stagedUsers }, ref) => {

    const { formatMessage } = useIntl();
    const { permissions } = usePartnerCreateEdit();
    const { catchWithToast, onSuccessToast } = useNotification();
    const userDefaultLookupSearchFields = useUserDefaultLookupSearchFields();
    const userCreateDialogRef = useRef<UserCreateDialogHandle>(null);
    const userEditDialogRef = useRef<UserEditDialogHandle>(null);
    const gmetrixConfirmRef = useRef<any>(null);

    const [state, setState] = useSuperState({
        loadingGridData: true,
        searchTerm: '',
        users: [] as User[]
    });

    useImperativeHandle(ref, () => ({
        openAddDialog: () => userCreateDialogRef.current?.show()
    }));

    const { loadingGridData, searchTerm, users } = state;

    const filteredUsers = useMemo(() => users.filter(u => (
        u.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.LastName?.toLowerCase().includes(searchTerm.toLowerCase())
    )), [users, searchTerm]);

    // Build PartnerPermissions-compatible object for UserCreateDialog
    const partnerPermissions: PartnerPermissions = {
        AdvancedEdit: permissions.IsSuperAdmin,
        Authenticate: false,
        Create: permissions.CanCreate,
        Edit: permissions.CanCreate || permissions.CanEdit,
        GroupFeatureFlag: false,
        MergePartner: false,
        OrderManagementFeatureFlag: false,
        PlaceAnOrder: false,
        UserEdit: true,
        UserView: true,
        View: true
    };

    const getAllUsers = async () => {

        try {

            const fetchedUsers = await UsersApi.GetAll(1, partnerId);
            setState({ users: fetchedUsers });
            onUserCountChange?.(fetchedUsers.length);

        } catch (e) {

            console.error(e);

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const onDetailsItemClick = async (dataItem: User) => {

        userEditDialogRef.current?.show(dataItem);

    };

    const onRemoveUser = async (dataItem: User) => {

        const result = await gmetrixConfirmRef.current?.confirm(
            formatMessage({ id: 'remove_user.4584c8fe', defaultMessage: 'Remove User' }) || 'Remove User',
            formatMessage({ id: 'are_you_sure_you_want_to_remove_this_use.812e25c4', defaultMessage: 'Are you sure you want to remove this user? There is no undoing this action.' }) || 'Are you sure you want to remove this user? There is no undoing this action.'
        );
        if (result) {

            await UsersApi.RemoveUserFromTC(dataItem.UserProfileID, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_remove_user.66da10b8', defaultMessage: 'Failed to remove user.' })));
            getAllUsers();

        }

    };

    // Full page navigation (not AJAX) to the server-side LoginAsAdmin endpoint.
    // SpoofAdminLogin sets auth cookies and redirects server-side, so it must be
    // a direct browser request rather than a fetch/API call.
    const onLoginAsUser = (dataItem: User) => {

        window.location.href = `/Api/Services/LoginAsAdmin?userId=${dataItem.Id}`;

    };

    const customCreateClick = async (newPartnerUserObj: NewUser, permissionsCSV: string, existingUser: boolean, allowUserToSetPassword: boolean) => {

        userCreateDialogRef.current?.hide();

        if (stagedMode) {

            onStageUser?.({ allowUserToSetPassword, existingUser, permissionsCSV, user: newPartnerUserObj });
            return;

        }

        try {

            if (existingUser) {

                await UsersApi.Update(newPartnerUserObj, permissionsCSV, partnerId, partnerType);

            } else {

                await UsersApi.Create(newPartnerUserObj, permissionsCSV, partnerId, partnerType, allowUserToSetPassword);

            }

            onSuccessToast(formatMessage({ id: 'succesfully_created_user.ca91eb24', defaultMessage: 'Succesfully Created User' }) || 'Succesfully Created User');

        } catch (e) {

            catchWithToast(e, existingUser ? formatMessage({ id: 'failed_to_update.419f8ec3', defaultMessage: 'Failed to update' }) : formatMessage({ id: 'failed_to_create_user.99982644', defaultMessage: 'Failed to create user.' }));

        } finally {

            getAllUsers();

        }

    };

    // Convert staged users to User objects for table display
    const stagedUsersAsTableData: User[] = stagedMode && stagedUsers
        ? stagedUsers.map((s, index) => ({
            CurrentPartnerId: 0,
            CurrentUserId: 0,
            DisabledDetailsBtn: true,
            EarlyAccessPreference: 0 as any,
            Email: s.user.Email || s.user.UserName,
            FeatureRingId: 0,
            FirstName: s.user.FirstName,
            FullName: `${s.user.FirstName} ${s.user.LastName}`,
            Id: String(index),
            LastName: s.user.LastName,
            PartnerActive: true,
            PartnerInviteText: '',
            PartnerList: [],
            Permissions: [],
            PhoneNumber: s.user.PhoneNumber || '',
            UserName: s.user.UserName || s.user.Email,
            UserProfileID: index
        }))
        : [];

    const stagedColumnData: any[] = [
        { field: 'FirstName', title: formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) || 'First Name' },
        { field: 'LastName', title: formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) || 'Last Name' },
        { field: 'Email', title: formatMessage({ id: 'email.500d9e86', defaultMessage: 'Email' }) || 'Email' },
        {
            cell: ({ dataItem }: { dataItem: User }) => (
                <td>
                    <div className="d-flex justify-content-end">
                        <GMetrixButton
                            onClick={() => onRemoveStagedUser?.(dataItem.UserProfileID)}
                            variant="outline-danger"
                        >
                            {<FormattedMessage defaultMessage="Remove" id="remove.d34ac1e7" /> || 'Remove'}
                        </GMetrixButton>
                    </div>
                </td>
            ),
            field: 'actions' as any,
            title: '',
            width: 160
        }
    ];

    return (
        <>
            <GMetrixDialogConfirm ref={gmetrixConfirmRef} />
            <UserGMetrixTable
                columnData={stagedMode ? stagedColumnData : [
                    { field: 'UserProfileID', hidden: true, title: formatMessage({ id: 'id.e9650a61', defaultMessage: 'ID' }) || 'ID' },
                    { field: 'FirstName', title: formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) || 'First Name' },
                    { field: 'LastName', title: formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) || 'Last Name' },
                    { field: 'Email', title: formatMessage({ id: 'email.500d9e86', defaultMessage: 'Email' }) || 'Email' },
                    { field: 'UserName', title: formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' }) || 'Username' },
                    { field: 'PartnerActive', title: formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' }) || 'Active' },
                    { field: 'PartnerInviteText', title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }) || 'Status' },
                    {
                        cell: ({ dataItem }) => (
                            <td className="d-flex justify-content-end" role="gridcell">
                                <ButtonGroup>
                                    <GMetrixButton
                                        onClick={() => onDetailsItemClick(dataItem)}
                                        variant="outline-primary"
                                    >
                                        {<FormattedMessage defaultMessage="Edit" id="edit.00978daf" /> || 'Edit'}
                                    </GMetrixButton>
                                    <GMetrixButtonDropdown
                                        dataItems={[
                                            ...(permissions.IsSuperAdmin ? [{
                                                action: () => onLoginAsUser(dataItem),
                                                iconClass: 'fa-right-to-bracket text-primary',
                                                text: formatMessage({ id: 'login_as_user.84767b89', defaultMessage: 'Login as User' }) || 'Login as User'
                                            }] : []),
                                            {
                                                action: () => onRemoveUser(dataItem),
                                                iconClass: 'fa-circle-xmark text-danger',
                                                text: formatMessage({ id: 'remove.d34ac1e7', defaultMessage: 'Remove' }) || 'Remove'
                                            }
                                        ]}
                                        iconClass="fa-solid fa-ellipsis-vertical"
                                        variant="outline-primary"
                                    />
                                </ButtonGroup>
                            </td>
                        ),
                        field: 'actions' as any,
                        locked: true,
                        title: ' ',
                        width: 180
                    }
                ]}
                advancedSearchFields={userDefaultLookupSearchFields}
                allowExport={!stagedMode}

                defaultItemsPerPage={10}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                getAllUsers={stagedMode ? () => { } : getAllUsers}
                loadingGridData={stagedMode ? false : loadingGridData}
                manuallyRenderUsers={stagedMode}
                partnerId={partnerId}
                searchAutoFocus={searchAutoFocus}
                searchTerm={searchTerm}
                setLoadingGridData={val => setState({ loadingGridData: typeof val === 'function' ? val(state.loadingGridData) : val })}
                setSearchTerm={val => setState({ searchTerm: val })}
                setUsers={val => setState({ users: val })}
                toggleColumns={!stagedMode}
                users={stagedMode ? stagedUsersAsTableData : filteredUsers}
                viewPermission={true}
            />
            <UserCreateDialog
                customCreateClick={customCreateClick}
                page="Partner"
                permissions={partnerPermissions}
                ref={userCreateDialogRef}
                setUsers={val => setState({ users: typeof val === 'function' ? val(state.users) : val })}
                specificPartner={partnerType}
            />
            <UserEditDialog
                onSaved={() => getAllUsers()}
                partnerId={partnerId}
                permissions={partnerPermissions}
                ref={userEditDialogRef}
            />
        </>
    );

});

UsersSection.displayName = 'UsersSection';
